import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { reportRangeSearchSchema } from '@/features/reports/components/report-range-search'
import { SalesBookPage } from '@/features/reports/pages/sales-book'
import { requireCapability } from '@/lib/auth'

import type { ReportRangeSelection, SalesFilters, SalesPaging } from '@/types'

const PAGE_SIZE = 50

/* Reports → Sales: the accountant's sales book. The range, the comparison,
   the list and every drill-down filter live in the URL, so "the refunds in
   March" can be shared. Charts are in Analytics → Sales. */
const searchSchema = reportRangeSearchSchema.extend({
  compare: z.enum(['prev', 'year']).optional(),
  tab: z.enum(['summary', 'tax', 'payments', 'details']).optional(),
  view: z.enum(['daily', 'items', 'budtenders', 'log', 'tax', 'orders']).optional(),
  groupBy: z.enum(['category', 'product', 'brand', 'supplier', 'customerType', 'channel']).optional(),
  has: z.string().optional(),
  kind: z.enum(['discount', 'refund', 'void']).optional(),
  tender: z.string().optional(),
  day: z.string().optional(),
  customerType: z.string().optional(),
  channel: z.string().optional(),
  taxKey: z.string().optional(),
  cannabis: z.enum(['yes', 'no']).optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(200).optional(),
})

export const Route = createFileRoute('/_dashboard/reports/sales')({
  beforeLoad: requireCapability('VIEW_REPORTS'),
  validateSearch: searchSchema,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: SalesReportRoute,
})

function SalesReportRoute() {
  const { compare, tab, view, groupBy, has, kind, tender, day, customerType, channel, taxKey, cannabis, page, limit, ...range } = Route.useSearch()
  const navigate = Route.useNavigate()
  // A sales book is read by the month: default to this month.
  const selection: ReportRangeSelection = range.mode === 'preset' && !range.preset ? { ...range, mode: 'month' } : range
  const filters: SalesFilters = { compare, tab, view, groupBy, has, kind, tender, day, customerType, channel, taxKey, cannabis }
  const paging: SalesPaging = { page: page ?? 1, limit: limit ?? PAGE_SIZE }
  return (
    <SalesBookPage
      selection={selection}
      onSelectionChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true })}
      filters={filters}
      onFiltersChange={(next) =>
        navigate({
          search: (prev) => ({
            ...prev, ...next,
            compare: next.compare === 'prev' ? undefined : next.compare,
            tab: next.tab === 'summary' ? undefined : next.tab,
            view: next.view === 'daily' ? undefined : next.view,
            page: undefined,
          }),
          replace: true,
        })
      }
      paging={paging}
      onPagingChange={(next) => navigate({ search: (prev) => ({ ...prev, page: next.page > 1 ? next.page : undefined, limit: next.limit !== PAGE_SIZE ? next.limit : undefined }), replace: true })}
    />
  )
}
